import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import { AddTeamMemberMethod, AddTeamMemberView } from "./AddUsers.model";
import { sendInvite } from '@shared/services/TeamService';
import { loadAccountInfo } from '@features/settings/store/payments.duck';
import { BasicUserInfo } from '@shared/models/user.models';
import { TeamEntity } from '@shared/models/team.models';
import { UpdateUserField, updateUserField } from '@features/settings/services/UserService';
import { enrichError, ErrorCode, parseErrorFromException } from '@shared/utils/error-helper';
import { loadConfig } from "@shared/store/config.duck";
import { failureNotification } from "@shared/store/notification.duck";

export interface AddUsersState {
  isLoading: boolean;
  addTeamMemberMethod: AddTeamMemberMethod;
  view: AddTeamMemberView;
  error?: string;
  errorCode?: ErrorCode;
  team?: TeamEntity;
}

export const initialAddUsersState: AddUsersState = {
  isLoading: false,
  addTeamMemberMethod: AddTeamMemberMethod.NewUser,
  view: AddTeamMemberView.AddUser,
}

export const addNewUser: any = createAsyncThunk(
  'addUsers/addNewUser',
  async (params: {firstname: string, lastname: string, emailAddress: string, teamId: string, defaultLocationId?: number}, thunkAPI) => {
    try {
      await sendInvite(params.emailAddress, params.firstname, params.lastname, params.teamId, params.defaultLocationId);
      await thunkAPI.dispatch(loadAccountInfo());
      await thunkAPI.dispatch(loadConfig());
    } catch (e: any) {
      throw e;
    }
  }
)

export const moveUser: any = createAsyncThunk(
  'addUsers/moveUser',
  async (params: {user: BasicUserInfo, team: TeamEntity}, thunkAPI) => {
    try {
      await updateUserField(params.user.userId, UpdateUserField.team, params.team.id)
      await thunkAPI.dispatch(loadConfig());
    } catch (e: any) {
      thunkAPI.dispatch(failureNotification('Failed to move user'))
      throw e;
    }
  }
)

const addUsersSlice = createSlice({
  name: 'addUsers',
  initialState: initialAddUsersState,
  reducers: {
    reset: () => ({...initialAddUsersState}),
    setAddTeamMemberMethod: (state, action) =>
      ({...state, addTeamMemberMethod: action.payload, error: undefined, errorCode: undefined}),
    setAddTeamMemberView: (state, action) =>
      ({...state, view: action.payload, error: undefined, errorCode: undefined}),
    setSelectedTeam: (state, action) =>
      ({...state, team: action.payload}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(addNewUser.pending, (state) => ({...state, isLoading: true, error: undefined, errorCode: undefined}))
      .addCase(addNewUser.rejected, (state, action) => {
        const error = parseErrorFromException(action.error);
        return {...state, isLoading: false, error, errorCode: enrichError(error).id};
      })
      .addCase(addNewUser.fulfilled, (state, action) => ({...state, isLoading: false, view: AddTeamMemberView.UserAddedSuccessful, error: undefined, errorCode: undefined}))
      .addCase(moveUser.pending, (state) => ({...state, isLoading: true}))
      .addCase(moveUser.rejected, (state, action) => ({...state, isLoading: false}))
      .addCase(moveUser.fulfilled, (state, action) => ({...state, isLoading: false, view: AddTeamMemberView.UserMovedSuccessful}));
  }
})

export default addUsersSlice.reducer;
export const { reset, setAddTeamMemberMethod, setAddTeamMemberView, setSelectedTeam } = addUsersSlice.actions;

// Selectors
export const selectIsLoading = (state: AppState) => state.addUsers.isLoading;
export const selectAddTeamMemberMethod = (state: AppState) => state.addUsers.addTeamMemberMethod;
export const selectAddTeamMemberView = (state: AppState) => state.addUsers.view;
export const selectError = (state: AppState) => state.addUsers.error;
export const selectErrorCode = (state: AppState) => state.addUsers.errorCode;
export const selectSelectedTeam = (state: AppState) => state.addUsers.team;
